import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Check, Flag, Pencil, Trash2 } from 'lucide-react';

import { UserContext } from '../../context/UserContext';
import { formatAssistantMessageHtml } from '../AI/assistantMarkdown';
import { stableImageUrl } from '../../lib/stableImages';

const esUrlDirecta = (url) => Boolean(url && /^https?:\/\//i.test(url));

/**
 * URL servible del adjunto. Las http(s) (S3 presignada, prod) van directas; la
 * ruta relativa del proxy autenticado (/v1/…, storage local = staging) necesita
 * Authorization → se baja a blob, el mismo patrón que los DNI en CustomerModal.
 */
function useAdjuntoSrc(url) {
  const [token] = useContext(UserContext);
  // El token vive en una ref y NO en las deps del efecto: al refrescarse (cada
  // 45 min) el efecto se re-ejecutaba, su limpieza revocaba el blob que la
  // imagen seguía mostrando y el adjunto se rompía a la vista del usuario.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const [src, setSrc] = useState(esUrlDirecta(url) ? url : null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (!url || esUrlDirecta(url)) {
      setSrc(url || null);
      setFallo(false);
      return undefined;
    }
    let blobUrl = null;
    let cancelado = false;
    setFallo(false);
    const base = (axios.defaults.baseURL || '').replace(/\/$/, '');
    fetch(`${base}${url}`, { headers: { Authorization: `Bearer ${tokenRef.current}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((b) => {
        if (!cancelado) {
          blobUrl = URL.createObjectURL(b);
          setSrc(blobUrl);
        }
      })
      // Sin esto, un 403/500 dejaba «Cargando adjunto…» para siempre: el
      // usuario no sabía si tardaba o si estaba roto.
      .catch(() => {
        if (!cancelado) setFallo(true);
      });
    return () => {
      cancelado = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [url]);

  return { src, fallo };
}

function inicial(nombre) {
  return (nombre || '?').trim().charAt(0).toUpperCase() || '?';
}

function horaCorta(iso) {
  const fecha = new Date(iso);
  if (Number.isNaN(fecha.getTime())) return '';
  return fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function Adjunto({ msg }) {
  const { src, fallo } = useAdjuntoSrc(msg.attachment_url);
  if (!msg.attachment_url) return null;
  if (fallo) {
    return (
      <p className="mt-1.5 text-xs text-red-600">
        No se pudo cargar el adjunto. Recarga la página para volver a intentarlo.
      </p>
    );
  }
  if (!src) {
    return <p className="mt-1.5 text-xs text-gray-400">Cargando adjunto…</p>;
  }
  const esImagen = (msg.attachment_mime || '').startsWith('image/');
  if (esImagen) {
    // stableImageUrl solo para URLs presignadas (anti-parpadeo, SCRUM-74);
    // un blob local ya es estable por definición.
    const imgSrc = esUrlDirecta(msg.attachment_url) ? stableImageUrl(src) : src;
    return (
      <a href={src} target="_blank" rel="noreferrer" className="mt-1.5 block max-w-xs">
        <img
          src={imgSrc}
          alt={msg.attachment_name || 'adjunto'}
          className="max-h-64 w-auto rounded-lg border border-gray-200 object-contain"
          loading="lazy"
        />
      </a>
    );
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
    >
      📎 {msg.attachment_name || 'documento'}
    </a>
  );
}

/**
 * Un mensaje del canal, estilo fila (Slack), no burbuja: autor + hora + cuerpo.
 * Variantes: usuario (texto plano pre-wrap), agente (markdown seguro del
 * asistente, F5), sistema (línea gris), tombstone (borrado).
 */
export default function MessageItem({ msg, isOwn, onEditar, onBorrar, onFlag }) {
  // Confirmación visual breve (check en vez de banderín) tras marcar: no hay
  // sistema de toasts en Smack, y añadir uno por esto sería sobredimensionado.
  const [recienMarcado, setRecienMarcado] = useState(false);

  const marcarPendiente = async () => {
    if (recienMarcado) return;
    const ok = await onFlag?.(msg);
    if (ok !== false) {
      setRecienMarcado(true);
      setTimeout(() => setRecienMarcado(false), 1500);
    }
  };

  if (msg.deleted) {
    return (
      <div className="px-4 py-1 text-xs italic text-gray-400">mensaje eliminado</div>
    );
  }

  if (msg.author_type === 'system') {
    return (
      <div className="px-4 py-1.5 text-center text-xs text-gray-400">{msg.body}</div>
    );
  }

  const esAgente = msg.author_type === 'agent';
  const nombre = esAgente ? 'Agente IA' : msg.author_name || 'usuario';

  return (
    <div className="group flex gap-2.5 px-4 py-1.5 hover:bg-gray-50">
      {/* #FF5400 (avatar de usuario): acento de marca de Smack (decisión 22-ago: se queda) — fuera de tokens.css a propósito: el design system es slate y este naranja es exclusivo del módulo. */}
      <span
        className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white ${
          esAgente ? 'bg-purple-500' : 'bg-[#FF5400]'
        }`}
        aria-hidden="true"
      >
        {esAgente ? 'IA' : inicial(nombre)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-gray-900">{nombre}</span>
          <span className="text-[11px] text-gray-400">{horaCorta(msg.created_at)}</span>
          {msg.edited_at ? <span className="text-[11px] text-gray-400">(editado)</span> : null}
        </p>
        {esAgente ? (
          <div
            className="text-sm leading-relaxed text-gray-800 [&_table]:my-2 [&_table]:text-xs"
            // formatAssistantMessageHtml escapa el HTML antes de aplicar su
            // markdown mínimo — mismo camino que el asistente, con test propio.
            dangerouslySetInnerHTML={{ __html: formatAssistantMessageHtml(msg.body) }}
          />
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800">{msg.body}</p>
        )}
        <Adjunto msg={msg} />
      </div>
      <span className="hidden shrink-0 items-start gap-1 pt-1 group-hover:flex">
        {/* #FF5400: acento de marca de Smack (decisión 22-ago: se queda) — fuera
            de tokens.css a propósito: el design system es slate y este naranja
            es exclusivo del módulo. Sin restricción de isOwn: cualquiera con
            acceso al canal puede marcar cualquier mensaje (incluido uno del
            agente IA) como pendiente. */}
        <button
          type="button"
          onClick={marcarPendiente}
          disabled={recienMarcado}
          className={`rounded p-1 hover:bg-gray-200 ${
            recienMarcado ? 'text-[#FF5400]' : 'text-gray-400 hover:text-[#FF5400]'
          }`}
          aria-label="Marcar como pendiente"
          title="Marcar como pendiente"
        >
          {recienMarcado ? <Check size={14} /> : <Flag size={14} />}
        </button>
        {isOwn && msg.author_type === 'user' && (
          <>
            <button
              type="button"
              onClick={() => onEditar?.(msg)}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-600"
              aria-label="Editar mensaje"
            >
              <Pencil size={14} />
            </button>
            <button
              type="button"
              onClick={() => onBorrar?.(msg)}
              className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600"
              aria-label="Borrar mensaje"
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
      </span>
    </div>
  );
}
